/**
 * BankAccountsTabBase — wspólna implementacja zakładki Konta bankowe (kontrahent + firma).
 *
 * Wyekstrahowana z `BankAccountsDynamicTab.tsx` (kontrahent, synchronizacja z białą
 * listą VAT MF) i `CompanyBankAccountsTab.tsx` (firma, bez whitelist) — oba komponenty
 * były ~90% identyczne (fetch/mutacje/modal/helpery), różniąc się wyłącznie: bazą API,
 * kształtem odpowiedzi (array vs `{bankAccounts:[...]}`), slugiem modułu/kluczem
 * zapytania i obsługą białej listy VAT MF (feature contractors-only — companies nie
 * mają MF whitelist).
 *
 * Renderowanie listy przeniesione na `ConfigurableTable` (filtrowanie/sortowanie/
 * sesyjny column-chooser, `persistConfig={false}` — zero zależności od backendu i
 * uprawnienia `crm_core.system_modules.manage`) — modal create/edit
 * (`RecursiveFormRenderer` na layoucie FORM) i wszystkie mutacje bez zmian.
 *
 * `whitelist: null` wyłącza JEDNYM warunkiem: kolumnę statusu, akcję per-wiersz
 * „Zweryfikuj na białej liście" i przycisk synchronizacji w nagłówku + `WhitelistSyncModal`.
 */

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Landmark } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  type LayoutConfig,
  type LayoutElement,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { ExtFieldValueInput } from '@/components/system-modules/ExtFieldValueInput';
import { useToast } from '@/contexts/ToastContext';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { api } from '@/lib/api';
import { applyOrder } from '@/lib/kartoteki/formatka-order';
import { slugToCamel } from '@/lib/slug-to-camel';

import {
  type BankAccountRow,
  buildBankAccountColumns,
  type FieldsResponse,
  readField,
  type SystemModuleField,
} from './bank-accounts-columns';
import { WhitelistSyncModal } from './WhitelistSyncModal';

export interface BankAccountsTabWhitelistConfig {
  /** NIP kontrahenta (surowy, może zawierać myślniki/spacje) — steruje dostępnością
   *  przycisku „Synchronizuj z białą listą" (wymaga dokładnie 10 cyfr po oczyszczeniu). */
  taxId: string | null;
  contractorId: string;
}

export interface BankAccountsTabConfig {
  moduleSlug: 'contractor_bank_accounts' | 'company_bank_accounts';
  apiBase: string;
  queryKey: readonly unknown[];
  unwrapList: (data: unknown) => BankAccountRow[];
  /** `null` = wariant firmowy: brak kolumny/akcji/przycisku synchronizacji whitelist. */
  whitelist: BankAccountsTabWhitelistConfig | null;
  /**
   * Slugi kolumn UKRYTYCH z poziomu layoutu (formatka bloku w kreatorze, ikonka oka).
   * Zapisane trwale w `settings.hiddenColumnIds` bloku `contractor_bank_accounts_view`.
   * Odfiltrowywane z `listColumns` — nowe pola są domyślnie widoczne (hide-lista, nie
   * allowlista). `undefined`/puste = wszystkie kolumny widoczne (back-compat).
   */
  hiddenColumnIds?: readonly string[];
  /** Kolejność kolumn (slugi) z formatki bloku — drag; nieujęte na końcu (back-compat). */
  columnOrder?: readonly string[];
}

export interface BankAccountsTabBaseProps {
  config: BankAccountsTabConfig;
  testIdPrefix: string;
}

export function BankAccountsTabBase({ config, testIdPrefix }: BankAccountsTabBaseProps) {
  const { moduleSlug, apiBase, queryKey, unwrapList, whitelist } = config;
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const formId = useId();

  // ── Fetchy ───────────────────────────────────────────────────────────────
  const accountsQ = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<unknown>(apiBase);
      return unwrapList(res.data);
    },
  });

  const fieldsQ = useQuery({
    queryKey: ['system-module-fields', moduleSlug],
    queryFn: async () => {
      const res = await api.get<FieldsResponse>(`/system-modules/${moduleSlug}/fields`);
      return res.data.fields;
    },
    staleTime: 2 * 60 * 1000,
  });

  const {
    layoutConfig,
    isLoading: layoutLoading,
    isError: layoutError,
  } = useSystemModuleActiveLayout(moduleSlug);

  // ── Modal state ──────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccountRow | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [showWhitelistSyncModal, setShowWhitelistSyncModal] = useState(false);

  const hasValidNip = whitelist !== null && isValidNip(whitelist.taxId);

  // ── Maps + helper ────────────────────────────────────────────────────────
  const fieldById = useMemo(() => {
    const map = new Map<string, SystemModuleField>();
    for (const f of fieldsQ.data ?? []) map.set(f.id, f);
    return map;
  }, [fieldsQ.data]);

  // Etap 10.3 (ADR-0066 §11) — `formValues` jest kluczowany CAMELCASE (`slugToCamel`),
  // a `dictionaryParamBindings` wskazuje slug-sąsiada RAW — bez tego mostka lookup w
  // `ExtFieldValueInput` nie znalazłby wartości sąsiada dla pola param-bound.
  const recordValuesBySlug = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const f of fieldsQ.data ?? []) {
      if (f.gcrecord !== null) continue;
      out[f.slug] = formValues[slugToCamel(f.slug)];
    }
    return out;
  }, [fieldsQ.data, formValues]);

  // Zawartość zakładki `data` z layoutu (rozpakowujemy tabs wrapper — modal
  // nie ma sensu pokazywać paska zakładek dla helper-encji z jednym tabem).
  const formLayoutConfig: LayoutConfig | null = useMemo(() => {
    if (!layoutConfig) return null;
    const tabsEl = layoutConfig.elements.find((el) => el.type === 'tabs');
    const tabs = tabsEl?.tabs ?? [];
    const dataTab = tabs.find((tt) => tt.slug === 'data') ?? tabs[0];
    if (!dataTab) return { elements: [] };
    return { elements: dataTab.children };
  }, [layoutConfig]);

  // Wszystkie pola z layoutu (nie tylko `showInList`, w odróżnieniu od dawnej
  // implementacji) — sesyjny column-chooser `ConfigurableTable` pozwala odkryć pola
  // domyślnie skryte (np. `swift_bic`); `buildBankAccountColumns` odtwarza dzisiejszy
  // domyślny widok przez `defaultVisible: field.showInList`.
  const listColumns = useMemo<SystemModuleField[]>(() => {
    if (!formLayoutConfig) return [];
    const hidden = new Set(config.hiddenColumnIds ?? []);
    const out: SystemModuleField[] = [];
    walkFieldsInLayout(formLayoutConfig.elements, (fieldId) => {
      const f = fieldById.get(fieldId);
      // Ukrywanie kolumn z layoutu (formatka bloku) — pomijamy pola na hide-liście.
      if (f && f.gcrecord === null && !hidden.has(f.slug)) out.push(f);
    });
    return out;
  }, [formLayoutConfig, fieldById, config.hiddenColumnIds]);

  // Kolejność kolumn z formatki bloku (drag) — `applyOrder` układa `listColumns` wg
  // `columnOrder`; nieujęte na końcu. Read-side == builder.
  const orderedListColumns = useMemo<SystemModuleField[]>(() => {
    const order = config.columnOrder;
    if (!order || order.length === 0) return listColumns;
    const bySlug = new Map(listColumns.map((c) => [c.slug, c]));
    return applyOrder(
      listColumns.map((c) => c.slug),
      order,
    )
      .map((slug) => bySlug.get(slug))
      .filter((c): c is SystemModuleField => !!c);
  }, [listColumns, config.columnOrder]);

  const accounts = useMemo(() => accountsQ.data ?? [], [accountsQ.data]);

  const tableColumns = useMemo(
    () =>
      buildBankAccountColumns({
        listColumns: orderedListColumns,
        accounts,
        whitelist: whitelist !== null,
        testIdPrefix,
        t,
      }),
    [orderedListColumns, accounts, whitelist, testIdPrefix, t],
  );

  // ── Open/close ───────────────────────────────────────────────────────────
  function openCreate() {
    setEditingAccount(null);
    setFormValues(buildInitialFormValues(fieldsQ.data ?? []));
    setFormErrors({});
    setShowModal(true);
  }

  function openEdit(account: BankAccountRow) {
    setEditingAccount(account);
    setFormValues(accountRowToFormValues(account, fieldsQ.data ?? []));
    setFormErrors({});
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setFormErrors({});
  }

  // ── Mutations ────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      setFormErrors({});
      const payload = buildPayload(formValues, fieldsQ.data ?? []);
      if (editingAccount) {
        const res = await api.patch(`${apiBase}/${editingAccount.id}`, payload);
        return res.data;
      }
      const res = await api.post(apiBase, payload);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.show({
        variant: 'success',
        title: t(
          editingAccount
            ? 'tenant.contractors.bankAccountsDynamic.toast.updated'
            : 'tenant.contractors.bankAccountsDynamic.toast.created',
        ),
      });
      closeModal();
    },
    onError: (err: unknown) => {
      const apiErr = extractApiError(err);
      if (apiErr.code === 'VALIDATION_ERROR' && apiErr.details) {
        const fieldErrors: Record<string, string> = {};
        for (const [key, msgs] of Object.entries(apiErr.details)) {
          if (Array.isArray(msgs) && msgs.length > 0) {
            fieldErrors[key] = translateValidationMessage(msgs[0]!);
          }
        }
        setFormErrors(fieldErrors);
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.bankAccountsDynamic.toast.validationError'),
        });
        return;
      }
      if (apiErr.code === 'INVALID_IBAN') {
        // Backend rzuca 422 INVALID_IBAN gdy nie przechodzi walidacji mod-97.
        setFormErrors({
          accountNumber: t('tenant.contractors.bankAccountsDynamic.fieldError.invalidIban'),
        });
        toast.show({
          variant: 'error',
          title: t('tenant.contractors.bankAccountsDynamic.toast.invalidIban'),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.bankAccountsDynamic.toast.saveError'),
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const res = await api.delete(`${apiBase}/${accountId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.bankAccountsDynamic.toast.deleted'),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.bankAccountsDynamic.toast.deleteError'),
      });
    },
  });

  // „Ustaw jako główne" — PATCH z `isPrimary: true` (backend sam swapuje poprzednie
  // konto główne na non-primary).
  const setPrimaryMutation = useMutation({
    mutationFn: async (accountId: string) => {
      await api.patch(`${apiBase}/${accountId}`, { isPrimary: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.bankAccounts.toast.primarySet', {
          defaultValue: 'Ustawiono jako konto główne',
        }),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.bankAccounts.toast.primaryError', {
          defaultValue: 'Nie udało się ustawić konta głównego',
        }),
      });
    },
  });

  // „Zweryfikuj na białej liście VAT MF" — tylko wariant kontrahenta (przycisk
  // renderowany warunkowo w `renderRowActions`); zdefiniowana bezwarunkowo (rules-of-hooks),
  // inertna dla wariantu firmowego.
  const verifyWhitelistMutation = useMutation({
    mutationFn: async (accountId: string) => {
      await api.post(`${apiBase}/${accountId}/verify-whitelist`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast.show({
        variant: 'success',
        title: t('tenant.contractors.bankAccounts.toast.verified', {
          defaultValue: 'Zweryfikowano na białej liście VAT MF',
        }),
      });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.contractors.bankAccounts.toast.verifyError', {
          defaultValue: 'Weryfikacja na białej liście nieudana',
        }),
      });
    },
  });

  // ── Render: loading guard ────────────────────────────────────────────────
  const initialLoading = accountsQ.isLoading || fieldsQ.isLoading || layoutLoading;

  if (initialLoading) {
    return (
      <div className="space-y-3 p-2" data-testid={`${testIdPrefix}-loading`}>
        <Skeleton className="h-8" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  // `fieldsQ.isError` obok `accountsQ.isError` — bez katalogu pól `listColumns` jest
  // pustą tablicą i tabela wyrenderowałaby się bez ŻADNEJ kolumny danych (dla wariantu
  // firmowego: całkiem bez treści) zamiast pokazać, że coś padło.
  if (accountsQ.isError || fieldsQ.isError) {
    return (
      <p className="text-sm text-red-600 py-4">
        {t('tenant.contractors.bankAccountsDynamic.error')}
      </p>
    );
  }

  if (layoutError || !formLayoutConfig || formLayoutConfig.elements.length === 0) {
    return (
      <p className="text-sm text-amber-600 py-4" data-testid={`${testIdPrefix}-no-layout`}>
        {t('tenant.contractors.bankAccountsDynamic.errorLayout')}
      </p>
    );
  }

  // ── renderField callback dla RecursiveFormRenderer (modal) ──────────────
  function renderField(fieldId: string) {
    const field = fieldById.get(fieldId);
    if (!field || field.gcrecord !== null) return null;
    const camelKey = slugToCamel(field.slug);
    const value = formValues[camelKey];
    const error = formErrors[camelKey];

    const errId = error ? `${testIdPrefix}-form-${field.slug}-error` : undefined;
    return (
      <FieldWrap
        key={fieldId}
        label={field.name}
        required={field.isRequired}
        error={error}
        errId={errId}
        colSpan={field.fieldType === 'text'}
      >
        <ExtFieldValueInput
          field={field}
          value={value}
          onChange={(v) => {
            setFormValues((prev) => ({ ...prev, [camelKey]: v }));
            if (error) {
              setFormErrors((prev) => {
                const next = { ...prev };
                delete next[camelKey];
                return next;
              });
            }
          }}
          error={error}
          aria-describedby={errId}
          data-testid={`${testIdPrefix}-form-${field.slug}`}
          recordValues={recordValuesBySlug}
        />
      </FieldWrap>
    );
  }

  function renderRowActions(row: BankAccountRow) {
    return (
      <div className="flex justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEdit(row)}
          className="rounded px-2 py-0.5 text-xs text-text-tertiary"
          data-testid={`${testIdPrefix}-edit-${row.id}`}
        >
          {t('common.edit', { defaultValue: 'Edytuj' })}
        </Button>
        {!row.isPrimary && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPrimaryMutation.mutate(row.id)}
            disabled={setPrimaryMutation.isPending}
            className="rounded px-2 py-0.5 text-xs text-accent-600"
            data-testid={`${testIdPrefix}-set-primary-${row.id}`}
          >
            {t('tenant.contractors.bankAccounts.actions.setPrimary', {
              defaultValue: 'Ustaw główne',
            })}
          </Button>
        )}
        {whitelist !== null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => verifyWhitelistMutation.mutate(row.id)}
            disabled={verifyWhitelistMutation.isPending}
            className="rounded px-2 py-0.5 text-xs text-text-tertiary"
            data-testid={`${testIdPrefix}-verify-whitelist-${row.id}`}
          >
            {t('tenant.contractors.bankAccounts.actions.verifyWhitelist', {
              defaultValue: 'Whitelist',
            })}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPendingDeleteId(row.id)}
          className="rounded px-2 py-0.5 text-xs text-red-500"
          data-testid={`${testIdPrefix}-delete-${row.id}`}
        >
          {t('common.delete', { defaultValue: 'Usuń' })}
        </Button>
      </div>
    );
  }

  // ── Render główny ────────────────────────────────────────────────────────
  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-primary">
          {t('tenant.contractors.bankAccountsDynamic.title')}
        </span>
        <div className="flex items-center gap-2">
          {whitelist !== null && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowWhitelistSyncModal(true)}
              disabled={!hasValidNip}
              title={
                hasValidNip
                  ? undefined
                  : t('tenant.contractors.bankAccounts.actions.syncWhitelistDisabledHint')
              }
              className="text-xs"
              data-testid={`${testIdPrefix}-sync-whitelist-btn`}
            >
              {t('tenant.contractors.bankAccounts.actions.syncWhitelist')}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={openCreate}
            className="text-xs"
            data-testid={`${testIdPrefix}-add-btn`}
          >
            {t('tenant.contractors.bankAccountsDynamic.addBtn')}
          </Button>
        </div>
      </div>

      <ConfigurableTable<BankAccountRow>
        tableId={`bank-accounts-tab-${moduleSlug}`}
        testIdPrefix={testIdPrefix}
        columns={tableColumns}
        data={accounts}
        persistConfig={false}
        showViewModeToggle={false}
        compact
        stickyHeader={false}
        renderActions={renderRowActions}
        emptyIcon={Landmark}
        emptyTitle={t('tenant.contractors.bankAccountsDynamic.empty')}
      />

      <Modal
        open={showModal}
        onClose={closeModal}
        title={t(
          editingAccount
            ? 'tenant.contractors.bankAccountsDynamic.modal.editTitle'
            : 'tenant.contractors.bankAccountsDynamic.modal.newTitle',
        )}
        size="lg"
        testId={`${testIdPrefix}-modal`}
        // Zapis w toku — tło i Esc nie zamykają okna (X i „Anuluj" działają).
        closeOnBackdrop={!saveMutation.isPending}
        closeOnEsc={!saveMutation.isPending}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              loading={saveMutation.isPending}
              disabled={saveMutation.isPending}
              data-testid={`${testIdPrefix}-form-submit`}
            >
              {saveMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      >
        <form
          id={formId}
          data-testid={`${testIdPrefix}-form`}
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
          className="space-y-3"
        >
          <RecursiveFormRenderer
            layoutConfig={formLayoutConfig}
            renderField={renderField}
            formData={formValues}
          />

          {Object.keys(formErrors).length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300"
              data-testid={`${testIdPrefix}-form-errors-banner`}
            >
              {t('tenant.contractors.bankAccountsDynamic.validationBanner')}
            </div>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title={t('common.confirmDelete')}
        message={t('tenant.contractors.bankAccountsDynamic.deleteConfirm')}
        onConfirm={() => {
          const id = pendingDeleteId;
          setPendingDeleteId(null);
          if (id) deleteMutation.mutate(id);
        }}
        onClose={() => setPendingDeleteId(null)}
      />

      {whitelist !== null && (
        <WhitelistSyncModal
          open={showWhitelistSyncModal}
          onClose={() => setShowWhitelistSyncModal(false)}
          contractorId={whitelist.contractorId}
        />
      )}
    </div>
  );
}

// ─── Pomocnicze: walidacja NIP (gate przycisku synchronizacji z białą listą) ─

/**
 * Poprawny NIP = dokładnie 10 cyfr po oczyszczeniu z myślników/spacji (format
 * z UI bywa `123-456-32-18` albo `1234563218`). Tylko gate frontendowy dla
 * dostępności przycisku — backend waliduje ponownie i zwraca 422
 * `CONTRACTOR_NIP_MISSING`.
 */
function isValidNip(taxId: string | null | undefined): boolean {
  if (!taxId) return false;
  const digits = taxId.replace(/\D/g, '');
  return digits.length === 10;
}

// ─── Pomocnicze: FieldWrap ─────────────────────────────────────────────────

function FieldWrap({
  label,
  required,
  colSpan,
  error,
  errId,
  children,
}: {
  label: string;
  required?: boolean;
  colSpan?: boolean;
  error?: string;
  errId?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={colSpan ? 'col-span-2' : undefined}>
      <label className="block text-xs font-medium text-text-tertiary mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <p id={errId} className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Helpery ─────────────────────────────────────────────────────────────

function walkFieldsInLayout(elements: LayoutElement[], visit: (fieldId: string) => void): void {
  for (const el of elements) {
    if (el.type === 'field' && el.fieldId) {
      visit(el.fieldId);
    }
    if (el.children) walkFieldsInLayout(el.children, visit);
    if (el.tabs) {
      for (const tab of el.tabs) walkFieldsInLayout(tab.children, visit);
    }
    if (el.accordionItems) {
      for (const item of el.accordionItems) walkFieldsInLayout(item.children, visit);
    }
    if (el.steps) {
      for (const step of el.steps) walkFieldsInLayout(step.children, visit);
    }
  }
}

function buildInitialFormValues(fields: SystemModuleField[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.gcrecord !== null) continue;
    const key = slugToCamel(f.slug);
    if (f.fieldType === 'boolean') {
      // Sensowne defaulty: isPrimary=false, isActive=true.
      out[key] = f.slug === 'is_active';
    } else if (f.slug === 'currency_code') {
      // Default PLN (najczęstszy w PL kontrahentach).
      out[key] = 'PLN';
    } else {
      out[key] = null;
    }
  }
  return out;
}

function accountRowToFormValues(
  account: BankAccountRow,
  fields: SystemModuleField[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.gcrecord !== null) continue;
    const key = slugToCamel(f.slug);
    out[key] = readField(account, key) ?? (f.fieldType === 'boolean' ? false : null);
  }
  return out;
}

function buildPayload(
  formValues: Record<string, unknown>,
  fields: SystemModuleField[],
): Record<string, unknown> {
  const allowedKeys = new Set<string>();
  for (const f of fields) {
    if (f.gcrecord !== null) continue;
    allowedKeys.add(slugToCamel(f.slug));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(formValues)) {
    if (!allowedKeys.has(k)) continue;
    out[k] = v === '' ? null : v;
  }
  return out;
}

interface ApiError {
  code: string | null;
  details?: Record<string, string[]>;
}

function extractApiError(err: unknown): ApiError {
  if (!err || typeof err !== 'object') return { code: null };
  const maybe = err as {
    response?: { data?: { error?: string; details?: Record<string, string[]> } };
  };
  return {
    code: maybe.response?.data?.error ?? null,
    details: maybe.response?.data?.details,
  };
}

/**
 * Tłumaczenie najczęstszych komunikatów Zod (po angielsku) na polski.
 */
function translateValidationMessage(raw: string): string {
  const exactly = raw.match(/exactly (\d+) character/i);
  if (exactly) return `Wymagane dokładnie ${exactly[1]} znaki`;

  const atMost = raw.match(/at most (\d+) character/i);
  if (atMost) return `Maksymalnie ${atMost[1]} znaków`;

  const atLeast = raw.match(/at least (\d+) character/i);
  if (atLeast) return `Minimum ${atLeast[1]} znaków`;

  if (/required/i.test(raw)) return 'Pole wymagane';
  if (/invalid email/i.test(raw)) return 'Nieprawidłowy email';
  if (/invalid url/i.test(raw)) return 'Nieprawidłowy adres URL';
  if (/invalid uuid/i.test(raw)) return 'Nieprawidłowy identyfikator';

  return raw;
}
